import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Button } from '@/components/common/UI/Button'
import { Card } from '@/components/common/UI/Card'
import { Input } from '@/components/common/UI/Input'

interface SalaryStats {
  position: string
  city: string
  min_salary: number
  max_salary: number
  avg_salary: number
  median_salary: number
  p25: number | null
  p75: number | null
  sample_size: number
  period_start: string
  period_end: string
}
interface SalaryTrend { month: string; average_salary: number; sample_size: number }

const money = (value: number | null | undefined) => value == null ? '—' : new Intl.NumberFormat('ru-RU').format(value) + ' ₽'

export default function SalaryCalculator() {
  const [position, setPosition] = useState('')
  const [city, setCity] = useState('')
  const [searched, setSearched] = useState<{ position: string; city: string } | null>(null)
  const search = useMutation({
    mutationFn: (params: { position: string; city?: string }) => apiClient.post<SalaryStats>('/v1/analytics/salary', params),
    onSuccess: (_, variables) => setSearched({ position: variables.position, city: variables.city || '' }),
  })
  const { data: trends = [] } = useQuery({
    queryKey: ['salary-trends', searched],
    queryFn: () => apiClient.get<SalaryTrend[]>('/v1/analytics/salary/trends', { params: { position: searched?.position, city: searched?.city || undefined, months: 12 } }),
    enabled: !!searched && !!search.data,
  })
  const stats = search.data

  return (
    <main className="container-custom mx-auto max-w-5xl px-4 py-10">
      <div className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">Данные вакансий LenJob</p>
        <h1 className="mt-2 text-3xl font-bold text-secondary-900">Калькулятор зарплат</h1>
        <p className="mt-3 text-secondary-600">Сравните зарплаты по профессии и городу на основе опубликованных вакансий.</p>
      </div>
      <Card className="mt-6 p-5">
        <form className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end" onSubmit={(event) => {
          event.preventDefault()
          if (position.trim().length >= 2) {
            const cityValue = city.trim()
            search.mutate({ position: position.trim(), ...(cityValue ? { city: cityValue } : {}) })
          }
        }}>
          <Input label="Профессия" placeholder="Например, Java-разработчик" value={position} onChange={(event) => setPosition(event.target.value)} required minLength={2} />
          <Input label="Город (необязательно)" placeholder="Москва" value={city} onChange={(event) => setCity(event.target.value)} />
          <Button type="submit" disabled={search.isPending}>{search.isPending ? 'Считаем…' : 'Рассчитать'}</Button>
        </form>
        {search.isError && <p className="mt-4 text-sm text-amber-800">Недостаточно опубликованных вакансий с указанной зарплатой. Попробуйте более широкое название роли или уберите город.</p>}
      </Card>

      {stats && <>
        <p className="mt-5 text-sm text-secondary-500">Выборка: {stats.sample_size} вакансий · данные за последние 90 дней</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Средняя', stats.avg_salary], ['Медианная', stats.median_salary], ['Нижняя граница', stats.min_salary], ['Верхняя граница', stats.max_salary],
          ].map(([label, value]) => <Card key={String(label)} className="p-5"><p className="text-sm text-secondary-500">{label}</p><p className="mt-2 text-xl font-bold">{money(Number(value))}</p></Card>)}
        </div>
        <Card className="mt-4 p-5">
          <h2 className="font-semibold">Диапазон 25–75%</h2>
          <p className="mt-2 text-secondary-700">{money(stats.p25)} — {money(stats.p75)}</p>
          <p className="mt-1 text-xs text-secondary-500">Показываем статистику только при выборке от пяти вакансий.</p>
        </Card>
        {trends.length > 0 && <Card className="mt-4 p-5">
          <h2 className="font-semibold">Средняя зарплата по месяцам</h2>
          <div className="mt-4 space-y-3">{trends.map((item) => <div key={item.month} className="grid grid-cols-[90px_1fr_auto] items-center gap-3 text-sm">
            <span>{new Date(item.month).toLocaleDateString('ru-RU', { month: 'short', year: 'numeric' })}</span>
            <div className="h-3 overflow-hidden rounded-full bg-secondary-100"><div className="h-full rounded-full bg-primary-600" style={{ width: `${Math.max(4, item.average_salary / Math.max(...trends.map((trend) => trend.average_salary)) * 100)}%` }} /></div>
            <strong>{money(item.average_salary)}</strong>
          </div>)}</div>
        </Card>}
      </>}
    </main>
  )
}
